import { useMutation, useQuery } from "@tanstack/react-query";
import base64 from "base64-js";

export interface BusinessPacket {
  id: string;
  gstin: string;
  status: string;
  createdAt: string;
  tokenExpiresAt: string;
  dataExpiresAt: string;
}

export interface StatusPacket {
  message: string;
  code: string;
}

export interface AllBusinessPacket extends Array<BusinessPacket> {}

export interface LogPacket {
  timestamp: string;
  message: string;
  code: string;
  error: string;
}

export interface AllLogPacket extends Array<LogPacket> {}

export function useBusinesses() {
  const baseUrl = getBaseURL();
  const auth = getAuth();

  return useQuery<AllBusinessPacket>({
    queryKey: ["businesses"],
    queryFn: () =>
      fetch(`${baseUrl}/business`, {
        method: "GET",
        headers: {
          authorization: auth || "",
        },
      }).then((res) => {
        if (!res.ok) {
          alert(res.statusText);
        }

        return res.json() as Promise<AllBusinessPacket>;
      }),
  }).data;
}

export function useBusiness(gstin: string) {
  const baseUrl = getBaseURL();
  const auth = getAuth();

  return useQuery<BusinessPacket>({
    queryKey: ["business", gstin],
    queryFn: () =>
      fetch(`${baseUrl}/business/${gstin}`, {
        method: "GET",
        headers: {
          authorization: auth || "",
        },
      }).then((res) => {
        if (!res.ok) {
          alert(res.statusText);
        }

        return res.json() as Promise<BusinessPacket>;
      }),
  }).data;
}

export function useDeleteBusinessMutation() {
  const baseUrl = getBaseURL();
  const auth = getAuth();

  return useMutation<StatusPacket, Error, { gstin: string }>({
    mutationFn: async ({ gstin }) => {
      const res = await fetch(`${baseUrl}/business/${gstin}`, {
        method: "DELETE",
        headers: {
          authorization: auth || "",
        },
      });
      if (!res.ok) {
        alert(await res.text());
      }

      return res.json() as Promise<StatusPacket>;
    },
  });
}

export function useRefreshBusinessMutation() {
  const baseUrl = getBaseURL();
  const auth = getAuth();

  return useMutation<StatusPacket, Error, { gstin: string }>({
    mutationFn: async ({ gstin }) => {
      const res = await fetch(`${baseUrl}/business/${gstin}/refresh`, {
        method: "POST",
        headers: {
          authorization: auth || "",
        },
      });
      if (!res.ok) {
        alert(await res.text());
      }

      return res.json() as Promise<StatusPacket>;
    },
  });
}

export function useOnboardMutation() {
  const baseUrl = getBaseURL();
  const auth = getAuth();

  return useMutation<StatusPacket, Error, { gstin: string; username: string }>({
    mutationFn: async ({ gstin, username }) => {
      const res = await fetch(`${baseUrl}/onboard/start`, {
        method: "POST",
        headers: {
          authorization: auth || "",
        },
        body: JSON.stringify({ gstin, username }),
      });
      if (!res.ok) {
        alert(await res.text());
      }

      return res.json() as Promise<StatusPacket>;
    },
  });
}

export function useVerifyOTPMutation() {
  const baseUrl = getBaseURL();
  const auth = getAuth();

  return useMutation<StatusPacket, Error, { gstin: string; otp: string }>({
    mutationFn: async ({ gstin, otp }) => {
      const res = await fetch(`${baseUrl}/onboard/otp`, {
        method: "POST",
        headers: {
          authorization: auth || "",
        },
        body: JSON.stringify({ gstin, otp }),
      });
      if (!res.ok) {
        alert(await res.text());
      }

      return res.json() as Promise<StatusPacket>;
    },
  });
}

export function useGetBasicInfoMutation() {
  const baseUrl = getBaseURL();
  const auth = getAuth();

  return useMutation<any, Error, { gstin: string }>({
    mutationFn: async ({ gstin }) => {
      const res = await fetch(`${baseUrl}/mcp/${gstin}/basic`, {
        method: "GET",
        headers: {
          authorization: auth || "",
        },
      });
      if (!res.ok) {
        alert(await res.text());
      }

      return res.json() as Promise<any>;
    },
  });
}

export function useGetGstr1DataMutation() {
  const baseUrl = getBaseURL();
  const auth = getAuth();

  return useMutation<any, Error, { gstin: string; period: string }>({
    mutationFn: async ({ gstin, period }) => {
      const res = await fetch(`${baseUrl}/mcp/${gstin}/gstr1/${period}`, {
        method: "GET",
        headers: {
          authorization: auth || "",
        },
      });
      if (!res.ok) {
        alert(await res.text());
      }

      return res.json() as Promise<any>;
    },
  });
}

export function useGetGstr2aDataMutation() {
  const baseUrl = getBaseURL();
  const auth = getAuth();

  return useMutation<any, Error, { gstin: string; period: string }>({
    mutationFn: async ({ gstin, period }) => {
      const res = await fetch(`${baseUrl}/mcp/${gstin}/gstr2a/${period}`, {
        method: "GET",
        headers: {
          authorization: auth || "",
        },
      });
      if (!res.ok) {
        alert(await res.text());
      }

      return res.json() as Promise<any>;
    },
  });
}

export function useGetGstr2bDataMutation() {
  const baseUrl = getBaseURL();
  const auth = getAuth();

  return useMutation<any, Error, { gstin: string; period: string }>({
    mutationFn: async ({ gstin, period }) => {
      const res = await fetch(`${baseUrl}/mcp/${gstin}/gstr2b/${period}`, {
        method: "GET",
        headers: {
          authorization: auth || "",
        },
      });
      if (!res.ok) {
        alert(await res.text());
      }

      return res.json() as Promise<any>;
    },
  });
}

export function useGetEinvoicePurchasesDataMutation() {
  const baseUrl = getBaseURL();
  const auth = getAuth();

  return useMutation<any, Error, { gstin: string; period: string }>({
    mutationFn: async ({ gstin, period }) => {
      const res = await fetch(
        `${baseUrl}/mcp/${gstin}/einvoice/purchases/${period}`,
        {
          method: "GET",
          headers: {
            authorization: auth || "",
          },
        },
      );
      if (!res.ok) {
        alert(await res.text());
      }

      return res.json() as Promise<any>;
    },
  });
}

export function useGetEinvoiceSalesDataMutation() {
  const baseUrl = getBaseURL();
  const auth = getAuth();

  return useMutation<any, Error, { gstin: string; period: string }>({
    mutationFn: async ({ gstin, period }) => {
      const res = await fetch(
        `${baseUrl}/mcp/${gstin}/einvoice/sales/${period}`,
        {
          method: "GET",
          headers: {
            authorization: auth || "",
          },
        },
      );
      if (!res.ok) {
        alert(await res.text());
        return;
      }

      return res.json() as Promise<any>;
    },
  });
}

export function useGetEinvoiceDetailsMutation() {
  const baseUrl = getBaseURL();
  const auth = getAuth();

  return useMutation<any, Error, { gstin: string; irn: string }>({
    mutationFn: async ({ gstin, irn }) => {
      const res = await fetch(`${baseUrl}/mcp/${gstin}/einvoice/${irn}`, {
        method: "GET",
        headers: {
          authorization: auth || "",
        },
      });
      if (!res.ok) {
        alert(await res.text());
      }

      return res.json() as Promise<any>;
    },
  });
}

export function downloadOpenAPIYAML({ gstin }: { gstin: string }) {
  const baseUrl = getBaseURL();
  const auth = getAuth();

  return fetch(`${baseUrl}/docs/${gstin}/openapi.yaml`, {
    method: "GET",
    headers: {
      authorization: auth || "",
    },
  }).then((res) => {
    if (!res.ok) {
      alert(res.text());
      return;
    }

    return res.blob().then((blob) => {
      downloadFile(blob, `${gstin}-openapi.yaml`);
    });
  });
}

export function downloadInstructionsMD({ gstin }: { gstin: string }) {
  const baseUrl = getBaseURL();
  const auth = getAuth();

  return fetch(`${baseUrl}/docs/${gstin}/instructions.md`, {
    method: "GET",
    headers: {
      authorization: auth || "",
    },
  }).then((res) => {
    if (!res.ok) {
      alert(res.text());
      return;
    }

    return res.blob().then((blob) => {
      downloadFile(blob, `${gstin}-instructions.md`);
    });
  });
}

export function useGetLogsQuery() {
  const baseUrl = getBaseURL();
  const auth = getAuth();

  return useQuery<AllLogPacket>({
    queryKey: ["logs"],
    queryFn: () =>
      fetch(`${baseUrl}/log`, { headers: { authorization: auth || "" } }).then(
        (res) => res.json(),
      ),
  });
}

function getBaseURL() {
  return localStorage.getItem("baseURL");
}

function getAuth() {
  const user = localStorage.getItem("user");
  const password = localStorage.getItem("password");
  if (!user || !password) {
    return null;
  }

  const encoded = base64.fromByteArray(
    new TextEncoder().encode(`${user}:${password}`),
  );
  return `Basic ${encoded}` as string;
}

function downloadFile(file: Blob, filename: string) {
  const url = URL.createObjectURL(file);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();

  URL.revokeObjectURL(url);
}
